























import { Component, Prop, Watch, Emit, Mixins } from "vue-property-decorator";
import { Point, Size, Rectangle, Vector, DraggingOffsets } from "../../../../../../base/domain/drawing";
import { AsyncRequestPool } from "../../../../../../base/utilities";
import { LocatableMixin, RectangleLayoutableMixin, LinearLayoutableMixin, PointsLayoutableMixin } from "../../../../../../base/components/system/mixins";
import { IFormatObjectEditable } from "../../../../../domain/editableFormat";
import {
  instanceOfILocationPropertyPresentable,
  instanceOfISizePropertyPresentable,
  instanceOfIRectangleCoordinateEditable,
  instanceOfIVectorCoordinateEditable,
} from "../../../../../domain/editableFormat/properties";
import { default as FormatObjectDrawer } from "../FormatObjectDrawer.vue";
import { IFormatPageLocatable, IFormatPageLocationResult } from "./IFormatPageLocatable";

@Component({
  components: {
    FormatObjectDrawer,
  },
})
export default class FormatPageLocator extends Mixins(LocatableMixin, RectangleLayoutableMixin, LinearLayoutableMixin, PointsLayoutableMixin) implements IFormatPageLocatable {
  @Prop()
  zIndex: number;

  data(): any {
    return {
      requestPool: new AsyncRequestPool<any, IFormatObjectEditable>(r => this.located(r.source)),
      source: null,
      offsets: new DraggingOffsets<IFormatObjectEditable>(null),
    };
  }
  requestPool: AsyncRequestPool<any, IFormatObjectEditable>;
  source: IFormatObjectEditable;
  offsets: DraggingOffsets<IFormatObjectEditable>;

  get rootStyle(): any {
    return {
      zIndex: this.zIndex,
    };
  }

  get message(): string {
    if (this.isLocating) {
      return "オブジェクトを追加する位置でクリックしてください。";
    } else if (this.isRectangleLayouting) {
      return "オブジェクトを配置する位置(左上)から、オブジェクトのサイズ分ドラッグしてください。";
    } else if (this.isLinearLayouting) {
      return "直線の始点から終点までドラッグしてください。";
    } else if (this.isPointsLayouting) {
      return "自由曲線の始点から終点までドラッグしてください。";
    } else {
      return "";
    }
  }

  get isVisible(): boolean {
    return this.isLocating
      || this.isRectangleLayouting
      || this.isLinearLayouting
      || this.isPointsLayouting;
  }

  get isObjectVisible(): boolean {
    return this.requestPool.isProcessing
      && (this.isLocatorVisible || this.isRectangleLayouterVisible || this.isLinearLayouterVisible || this.isPointsLayouterVisible);
  }

  @Watch("locatorRectangle")
  onLocatorRectangleUpdated(newValue: Rectangle, oldValue: Rectangle): void {
    this.onObjectRectangleUpdated(newValue, oldValue);
  }

  @Watch("layouterRectangle")
  onLayouterRectangleUpdated(newValue: Rectangle, oldValue: Rectangle): void {
    this.onObjectRectangleUpdated(newValue, oldValue);
  }

  private onObjectRectangleUpdated(newValue: Rectangle, oldValue: Rectangle): void {
    if (!this.isObjectVisible) {
      return;
    }
    if (instanceOfIRectangleCoordinateEditable(this.source)) {
      const size = this.source.size().getValue();
      this.offsets = new DraggingOffsets(
        this.source,
        newValue.topLeft,
        new Rectangle(
          new Point(1, 1),
          new Size(newValue.width / size.width, newValue.height / size.height)
        ),
        new Vector(new Point(0, 0), new Point(0, 0)),
      );
    }
  }

  @Watch("layouterLinear")
  onLayouterLinearUpdated(newValue: Vector, oldValue: Vector): void {
    if (!this.isObjectVisible) {
      return;
    }
    if (instanceOfIVectorCoordinateEditable(this.source)) {
      this.offsets = new DraggingOffsets(
        this.source,
        new Point(0, 0),
        new Rectangle(
          new Point(1, 1),
          new Size(1, 1)
        ),
        newValue,
      );
    }
  }

  @Watch("layouterPoints")
  onlayouterPointsUpdated(newValue: Point[], oldValue: Point[]): void {
    if (!this.isObjectVisible) {
      return;
    }
    // TODO: 自由線オブジェクト実装時に.
  }

  @Emit()
  located(located: IFormatObjectEditable): void {}

  async locateObject(requester: any, source: IFormatObjectEditable): Promise<IFormatPageLocationResult<Point>> {
    if (!instanceOfISizePropertyPresentable(source)) {
      throw Error("処理対象はサイズ情報を持っていません.");
    }
    return {
      result: await this.request<Point>(
        source,
        requester,
        () => this.locate(Size.from(source.size().getValue()))),
    };
  }

  async locateObjectVertically(requester: any, source: IFormatObjectEditable): Promise<IFormatPageLocationResult<Point>> {
    if (!instanceOfILocationPropertyPresentable(source) || !instanceOfISizePropertyPresentable(source)) {
      throw Error("処理対象は矩形情報を持っていません.");
    }
    return {
      result: await this.request<Point>(
      source,
      requester,
      () => this._locateVertically(new Rectangle(
        Point.from(source.location().getValue()),
        Size.from(source.size().getValue()))))
    };
  }

  async selectRectangle(requester: any): Promise<Rectangle> {
    return this.request<Rectangle>(
      null,
      requester,
      () => this.layoutRectangle(0),
    );
  }

  async layoutRectangleObject(requester: any, source: IFormatObjectEditable, fixedAspectRatio: number = 0): Promise<IFormatPageLocationResult<Rectangle>> {
    return {
      result: await this.request<Rectangle>(
        source,
        requester,
        () => this.layoutRectangle(fixedAspectRatio),
      )
    };
  }

  async layoutLinearObject(requester: any, source: IFormatObjectEditable): Promise<IFormatPageLocationResult<Vector>> {
    return {
      result: await this.request<Vector>(
        source,
        requester,
        () => this.layoutLinear(),
      )
    };
  }

  async layoutPointsObject(requester: any, source: IFormatObjectEditable): Promise<IFormatPageLocationResult<Point[]>> {
    return {
      result: await this.request<Point[]>(
        source,
        requester,
        () => this.layoutPoints(),
      )
    };
  }

  private async request<T>(source: IFormatObjectEditable, requester: any, request: () => Promise<any>): Promise<T> {
    this.source = source;
    const result = await this.requestPool.request(source, requester, request) as T;
    this.source = null;
    return result;
  }

  cancel(requester: any): void {
    if (this.requestPool.cancel(requester)) {
      // 実行中リクエストのキャンセル.
      this.cancelLocating();
      this.cancelRectangleLayouting();
      this.cancelLinearLayouting();
      this.cancelPointsLayouting();
    }
  }
}
